import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Search, Star } from "lucide-react";
import { fetchSchools } from "@/lib/api";
import { SCHOOLS } from "@/constants/testIds";
import { useT } from "@/lib/i18n";
import CountryFlag from "@/components/CountryFlag";

// Co-founder schools (Türkiye + Romania project leads) — pinned to the top of the directory.
const FOUNDER_SCHOOLS = new Set([
    "Istanbul TOKİ Kayaşehir Vocational and Technical High School",
    "Liceul Teoretic „Aurel Vlaicu\", Breaza (LTAV)",
    "Colegiul Tehnic „Stefan Procopiu\"",
]);

// Generic placeholders to demote to the bottom.
const PLACEHOLDER_SCHOOLS = new Set(["Partner School", "Partner Schools", "STEMCYCLE G2B Partners"]);

export default function SchoolsPage() {
    const { t } = useT();
    const [schools, setSchools] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const data = await fetchSchools();
                if (!cancelled) setSchools(data);
            } catch (e) {
                /* noop */
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        window.scrollTo({ top: 0, behavior: "instant" });
        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        const list = !ql
            ? schools
            : schools.filter(
                  (s) =>
                      s.name.toLowerCase().includes(ql) ||
                      (s.country || "").toLowerCase().includes(ql)
              );
        // Pin co-founder schools first, then placeholders last, then by count desc.
        return [...list].sort((a, b) => {
            const aFounder = FOUNDER_SCHOOLS.has(a.name) ? 0 : 1;
            const bFounder = FOUNDER_SCHOOLS.has(b.name) ? 0 : 1;
            if (aFounder !== bFounder) return aFounder - bFounder;
            const aPh = PLACEHOLDER_SCHOOLS.has(a.name) ? 1 : 0;
            const bPh = PLACEHOLDER_SCHOOLS.has(b.name) ? 1 : 0;
            if (aPh !== bPh) return aPh - bPh;
            return (b.contribution_count || 0) - (a.contribution_count || 0);
        });
    }, [schools, q]);

    const countries = useMemo(
        () => Array.from(new Set(schools.map((s) => s.country))).sort(),
        [schools]
    );

    return (
        <div data-testid={SCHOOLS.page}>
            <section className="container-pad pt-20 md:pt-28 pb-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <div className="caption text-brand-green mb-3">{t("schools.eyebrow")}</div>
                        <h1 className="heading-serif text-5xl md:text-6xl text-ink-primary leading-[0.95] tracking-tight text-balance">
                            {t("schools.heading1")}{" "}
                            <em className="gradient-g2b-text">{t("schools.heading2")}</em>
                        </h1>
                        <p className="mt-6 text-ink-secondary text-xl max-w-2xl leading-relaxed">
                            {t("schools.description", schools.length, countries.length)}
                        </p>
                    </div>

                    <div className="md:col-span-4">
                        <div className="relative">
                            <Search
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted"
                                size={16}
                                strokeWidth={1.6}
                            />
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={t("schools.searchPlaceholder")}
                                data-testid={SCHOOLS.searchInput}
                                className="w-full pl-11 pr-4 py-3 bg-white border border-black/10 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-pad pb-32">
                {loading ? (
                    <div className="space-y-3">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="h-20 rounded-xl bg-white border border-black/5 animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="rounded-2xl border border-black/5 bg-white overflow-hidden divide-y divide-black/5">
                        <div className="hidden md:grid md:grid-cols-12 px-8 py-5 bg-bg-alt/60 caption text-ink-secondary">
                            <div className="md:col-span-7">{t("schools.tableSchool")}</div>
                            <div className="md:col-span-3">{t("schools.tableCountry")}</div>
                            <div className="md:col-span-2 text-right">{t("schools.tableContributions")}</div>
                        </div>
                        {filtered.map((s, i) => {
                            const isFounder = FOUNDER_SCHOOLS.has(s.name);
                            return (
                                <motion.div
                                    key={`${s.name}-${s.country}`}
                                    initial={{ opacity: 0, y: 8 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.5, delay: Math.min(i * 0.02, 0.3) }}
                                    data-testid={SCHOOLS.row(i)}
                                    className={`grid grid-cols-1 md:grid-cols-12 px-8 py-6 items-center transition-colors duration-200 ${
                                        isFounder
                                            ? "bg-brand-gold/8 hover:bg-brand-gold/14"
                                            : "hover:bg-bg-alt/40"
                                    }`}
                                >
                                    <div className="md:col-span-7 flex items-start gap-3">
                                        {isFounder && (
                                            <span
                                                data-testid="cofounder-badge"
                                                className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-gold text-ink-primary text-[10px] uppercase tracking-[0.18em] font-semibold shrink-0"
                                            >
                                                <Star size={10} strokeWidth={2} fill="currentColor" />
                                                Co-founder
                                            </span>
                                        )}
                                        <div className="heading-serif text-[22px] text-ink-primary leading-snug">
                                            {s.name}
                                        </div>
                                    </div>
                                    <div className="md:col-span-3 mt-2 md:mt-0 flex items-center gap-2 text-[15px] text-ink-secondary">
                                        <CountryFlag country={s.country} size="md" />
                                        <MapPin size={14} strokeWidth={1.5} className="text-brand-blue" />
                                        {s.country}
                                    </div>
                                    <div className="md:col-span-2 mt-3 md:mt-0 md:text-right">
                                        <span className="inline-flex items-center justify-center min-w-[44px] px-3 py-1 rounded-full bg-brand-green/8 text-brand-green text-sm font-medium">
                                            {s.contribution_count}
                                        </span>
                                    </div>
                                </motion.div>
                            );
                        })}
                        {filtered.length === 0 && (
                            <div className="py-20 text-center text-ink-muted text-sm">
                                {t("schools.noMatch")}
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}
